import { useState } from "react";

import { AdminAvatar } from "@/components/admin/avatar";
import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { AdminIcon } from "@/components/admin/icon";
import { Tag } from "@/components/admin/tag";
import {
	useApprovalsList,
	useApproveApproval,
	useRejectApproval,
} from "@/hooks/use-admin-l4";
import { extractErrorMessage } from "@/hooks/use-auth-api";

export function ApprovalsTab() {
	const [filter, setFilter] = useState<string>("pending");
	const { data: list, refetch } = useApprovalsList(filter);
	const approveMutation = useApproveApproval();
	const rejectMutation = useRejectApproval();
	const [rejectId, setRejectId] = useState<string | null>(null);
	const [rejectNote, setRejectNote] = useState("");
	const [error, setError] = useState<string | null>(null);

	const handleApprove = async (id: string) => {
		setError(null);
		try {
			await approveMutation.mutateAsync({ id });
			refetch();
		} catch (err) {
			setError(extractErrorMessage(err, "Approbation impossible"));
		}
	};

	const handleReject = async () => {
		if (!rejectId || !rejectNote.trim()) return;
		setError(null);
		try {
			await rejectMutation.mutateAsync({
				id: rejectId,
				note: rejectNote.trim(),
			});
			setRejectId(null);
			setRejectNote("");
			refetch();
		} catch (err) {
			setError(extractErrorMessage(err, "Rejet impossible"));
		}
	};

	return (
		<div className="stack">
			<Card>
				<CardHead
					title="File des demandes (4-eyes)"
					sub="Actions sensibles nécessitant la validation d'un second utilisateur (CDC §4.5.1)"
					right={
						<div className="seg">
							{["pending", "approved", "rejected", "expired"].map((s) => (
								<button
									key={s}
									className={filter === s ? "on" : ""}
									onClick={() => setFilter(s)}
								>
									{s}
								</button>
							))}
						</div>
					}
				/>
				{error && (
					<CardBody>
						<div className="auth-alert danger" data-testid="approval-error">
							<span className="icon">
								<AdminIcon n="alert" s={16} />
							</span>
							<div>{error}</div>
						</div>
					</CardBody>
				)}
				<CardBody>
					{(!list || list.length === 0) && (
						<div className="empty">Aucune demande dans cette catégorie.</div>
					)}
					{list?.map((a) => (
						<div
							key={a.id}
							data-testid={`approval-${a.action}`}
							style={{
								display: "flex",
								gap: 12,
								padding: 14,
								borderRadius: 8,
								border: "1px solid var(--a-border)",
								marginBottom: 10,
								background:
									a.status === "pending"
										? "var(--a-warning-soft)"
										: "var(--a-surface)",
							}}
						>
							<AdminAvatar
								name={`${a.requestedBy.firstName} ${a.requestedBy.lastName}`}
								size="lg"
							/>
							<div style={{ flex: 1, minWidth: 0 }}>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: 8,
										marginBottom: 4,
									}}
								>
									<strong style={{ fontSize: 13 }}>{a.summary}</strong>
									<Tag
										variant={
											a.status === "pending"
												? "warning"
												: a.status === "approved"
													? "success"
													: a.status === "rejected"
														? "danger"
														: "neutral"
										}
									>
										{a.status}
									</Tag>
								</div>
								<div
									className="muted"
									style={{ fontSize: 12, marginBottom: 4 }}
								>
									Action <span className="mono">{a.action}</span> · Demandée par{" "}
									<strong>
										{a.requestedBy.firstName} {a.requestedBy.lastName}
									</strong>{" "}
									({a.requestedBy.email})
								</div>
								{a.reason && (
									<div
										style={{
											padding: 8,
											background: "var(--a-cream)",
											borderRadius: 4,
											fontStyle: "italic",
											fontSize: 12,
											color: "var(--a-ink-2)",
											marginBottom: 6,
										}}
									>
										« {a.reason} »
									</div>
								)}
								<div className="muted" style={{ fontSize: 11 }}>
									Soumise {new Date(a.createdAt).toLocaleString("fr-FR")} ·
									Expire {new Date(a.expiresAt).toLocaleString("fr-FR")}
								</div>
								{a.reviewedBy && (
									<div style={{ marginTop: 6, fontSize: 12 }}>
										{a.status === "approved" ? "Approuvée" : "Rejetée"} par{" "}
										<strong>
											{a.reviewedBy.firstName} {a.reviewedBy.lastName}
										</strong>
										{a.reviewerNote && (
											<span className="muted"> — {a.reviewerNote}</span>
										)}
									</div>
								)}
							</div>
							{a.status === "pending" && (
								<div
									style={{
										display: "flex",
										flexDirection: "column",
										gap: 6,
										alignItems: "flex-end",
									}}
								>
									<AdminButton
										variant="primary"
										size="sm"
										onClick={() => handleApprove(a.id)}
										data-testid={`approve-${a.action}`}
									>
										<AdminIcon n="check" s={12} />
										Approuver
									</AdminButton>
									<AdminButton
										variant="ghost"
										size="sm"
										style={{ color: "var(--a-danger)" }}
										onClick={() => setRejectId(a.id)}
										data-testid={`reject-${a.action}`}
									>
										<AdminIcon n="close" s={12} />
										Rejeter
									</AdminButton>
								</div>
							)}
						</div>
					))}
				</CardBody>
			</Card>

			{rejectId && (
				<Card>
					<CardHead title="Note de rejet (obligatoire)" />
					<CardBody>
						<textarea
							className="aform-i"
							rows={3}
							value={rejectNote}
							onChange={(e) => setRejectNote(e.target.value)}
							placeholder="Expliquez la raison du rejet..."
							data-testid="reject-note"
						/>
						<div
							style={{
								display: "flex",
								gap: 8,
								justifyContent: "flex-end",
								marginTop: 12,
							}}
						>
							<AdminButton variant="ghost" onClick={() => setRejectId(null)}>
								Annuler
							</AdminButton>
							<AdminButton
								variant="primary"
								onClick={handleReject}
								disabled={!rejectNote.trim() || rejectMutation.isPending}
								data-testid="reject-submit"
							>
								Confirmer le rejet
							</AdminButton>
						</div>
					</CardBody>
				</Card>
			)}
		</div>
	);
}
